import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

import { formatRequestWithRelations, RequestWithRelations } from './request';

export const useUpdateArticleActionSparePart = ({
  requestId,
  articleId,
  actionId,
  sparePartId,
  onSettled,
}: {
  requestId: string;
  articleId: string;
  actionId: string;
  sparePartId: string;
  onSettled?: () => void;
}) => {
  const queryClient = useQueryClient();
  const fetch =
    useFetch<Endpoints['PATCH /articles/:articleId/actions/:actionId/spare-parts/:sparePartId']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({
      quantity,
      referenceNumber,
      customOrganizationPrice,
      customWorkshopPrice,
    }: Endpoints['PATCH /articles/:articleId/actions/:actionId/spare-parts/:sparePartId']['body']) =>
      fetch(`/articles/${articleId}/actions/${actionId}/spare-parts/${sparePartId}`, undefined, {
        method: 'PATCH',
        body: {
          quantity,
          referenceNumber,
          customOrganizationPrice,
          customWorkshopPrice,
        },
      }),
    onMutate: (body) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: article.snapshot.articleActions.map((action) =>
                      action.id === actionId
                        ? {
                            ...action,
                            spareParts: action.spareParts.map((sparePart) =>
                              sparePart.id === sparePartId
                                ? {
                                    ...sparePart,
                                    ...body,
                                    customOrganizationPrice: body.customOrganizationPrice
                                      ? null
                                      : sparePart.customOrganizationPrice,
                                    customWorkshopPrice: body.customWorkshopPrice
                                      ? null
                                      : sparePart.customWorkshopPrice,
                                  }
                                : sparePart
                            ),
                          }
                        : action
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return { currentRequest };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });

      if (onSettled) {
        onSettled();
      }
    },
  });
};

export const useDeleteArticleActionSparePart = ({
  requestId,
  articleId,
  actionId,
}: {
  requestId: string;
  articleId: string;
  actionId: string;
}) => {
  const requestQueryKey = ['requests', requestId];
  const queryClient = useQueryClient();
  const fetch =
    useFetch<Endpoints['DELETE /articles/:articleId/actions/:actionId/spare-parts/:sparePartId']>();

  return useMutation({
    mutationFn: (sparePartId: string) =>
      fetch(`/articles/${articleId}/actions/${actionId}/spare-parts/${sparePartId}`, undefined, {
        method: 'DELETE',
      }),
    onMutate: (sparePartId: string) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: article.snapshot.articleActions.map((action) =>
                      action.id === actionId
                        ? {
                            ...action,
                            spareParts: action.spareParts.filter(
                              (sparePart) => sparePart.id !== sparePartId
                            ),
                          }
                        : action
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return { currentRequest };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export type SparePartTypeFromGetMany = Endpoints['GET /spare-part-types']['response'][number];
export const useGetSparePartTypesForAction = (
  {
    articleId,
    actionId,
    query,
  }: {
    articleId: string;
    actionId: string;
    query?: string;
  },
  options?: {
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /spare-part-types']>();

  return useQuery({
    queryKey: ['spare-part-types', 'article', articleId, 'action', actionId, 'query', query],
    queryFn: () =>
      fetch(
        '/spare-part-types',
        {
          articleId,
          articleActionId: actionId,
          query,
        },
        { method: 'GET' }
      ),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export type SparePartType = Endpoints['GET /spare-part-types']['response'][number];

export const TEMPORARY_ID_PREFIX = 'tmp-';

export const useAddSparePartToArticleAction = ({
  requestId,
  articleId,
  actionId,
}: {
  requestId: string;
  articleId: string;
  actionId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:articleId/actions/:actionId/spare-parts']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({ sparePartType }: { sparePartType: SparePartType }) =>
      fetch(`/articles/${articleId}/actions/${actionId}/spare-parts`, undefined, {
        method: 'POST',
        body: {
          id: sparePartType.id,
        },
      }),
    onMutate: ({ sparePartType }) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: article.snapshot.articleActions.map((action) =>
                      action.id === actionId
                        ? {
                            ...action,
                            spareParts: [
                              ...action.spareParts,
                              {
                                id: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                                stableId: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                                createdAt: new Date().toISOString(),
                                sparePartType,
                                quantity: 0,
                                quantityToBeCharged: 0,
                                articleActionId: action.id,
                                referenceNumber: null,
                                sparePartTypeId: sparePartType.id,
                                customOrganizationPrice: null,
                                customWorkshopPrice: null,
                                catalogOrganizationPrice: null,
                                catalogWorkshopPrice: null,
                              },
                            ],
                          }
                        : action
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export const useRestoreSparePartToArticleAction = ({
  requestId,
  articleId,
  actionId,
}: {
  requestId: string;
  articleId: string;
  actionId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch =
    useFetch<Endpoints['POST /articles/:articleId/actions/:actionId/spare-parts/reset']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({
      sparePart,
    }: {
      sparePart: {
        stableId: string;
        quantity: number;
        quantityToBeCharged: number;
        referenceNumber: string | null;
        sparePartType: Omit<SparePartType, 'workshopPrice' | 'organizationPrice'>;
      };
    }) =>
      fetch(`/articles/${articleId}/actions/${actionId}/spare-parts/reset`, undefined, {
        method: 'POST',
        body: {
          stableId: sparePart.stableId,
        },
      }),
    onMutate: ({ sparePart }) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: article.snapshot.articleActions.map((action) =>
                      action.id === actionId
                        ? {
                            ...action,
                            spareParts: [
                              ...action.spareParts,
                              {
                                id: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                                stableId: sparePart.stableId,
                                createdAt: new Date().toISOString(),
                                sparePartType: sparePart.sparePartType,
                                quantity: sparePart.quantity,
                                quantityToBeCharged: sparePart.quantityToBeCharged,
                                articleActionId: action.id,
                                referenceNumber: sparePart.referenceNumber,
                                sparePartTypeId: sparePart.sparePartType.id,
                                catalogOrganizationPrice: null,
                                catalogWorkshopPrice: null,
                                customOrganizationPrice: null,
                                customWorkshopPrice: null,
                              },
                            ],
                          }
                        : action
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};
